'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import { Loader2, Lock } from 'lucide-react';

import { Button } from '@/src/shared/ui/button';
import { Input } from '@/src/shared/ui/input';
import { Label } from '@/src/shared/ui/label';

import { registerSchema } from '../model/schemas';
import type { RegisterFormValues, RegisterState } from '../model/types';
import { registerAction } from '../api/registerAction';
import { RegisterSuccessPanel } from './RegisterSuccessPanel';
import { GoogleSignInButton } from './GoogleSignInButton';
import { EmailTakenNotice } from './EmailTakenNotice';
import { InvitedEmailHint } from './InvitedEmailHint';
import {
  getRegisterApiError,
  getRegisterFieldError,
  type RegisterFieldKey,
} from './mapRegisterError';
import { useInvitedEmailLock } from './useInvitedEmailLock';

const FIELDS: { key: RegisterFieldKey; type: string; autoComplete: string }[] = [
  { key: 'full_name', type: 'text', autoComplete: 'name' },
  { key: 'email', type: 'email', autoComplete: 'email' },
  { key: 'password', type: 'password', autoComplete: 'new-password' },
];

type Props = { invitationToken?: string | null };

export function RegisterForm({ invitationToken }: Props = {}) {
  const t = useTranslations('auth.register');
  const tSocial = useTranslations('auth.social');
  const params = useParams();
  const locale = (params.locale as string) ?? 'es';

  const [state, setState] = useState<RegisterState>('idle');
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const [emailTaken, setEmailTaken] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormValues>({ resolver: yupResolver(registerSchema) });

  const { invitedEmail, release } = useInvitedEmailLock(invitationToken, setValue);

  async function releaseInvitedEmail() {
    if (!(await release())) toast.error(t('invited.releaseFailed'));
  }

  async function onSubmit(data: RegisterFormValues) {
    setState('submitting');
    try {
      const result = await registerAction(data);
      if (!result.ok) {
        toast.error(getRegisterApiError(t, result.code));
        setEmailTaken(!!invitedEmail && result.code === 'email_taken');
        setState('error');
        return;
      }
      setRegisteredEmail(result.email);
      setState('success');
    } catch {
      toast.error(getRegisterApiError(t, 'server_error'));
      setState('error');
    }
  }

  if (state === 'success') {
    return <RegisterSuccessPanel email={registeredEmail} locale={locale} />;
  }

  const submitting = state === 'submitting';

  return (
    <form
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-4"
    >
      <GoogleSignInButton />

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" aria-hidden />
        <span>{tSocial('divider')}</span>
        <span className="h-px flex-1 bg-border" aria-hidden />
      </div>

      {FIELDS.map(({ key, type, autoComplete }) => {
        const errorCode = errors[key]?.message;
        const message = getRegisterFieldError(t, key, errorCode);
        const inputId = `register-${key}`;
        const locked = key === 'email' && !!invitedEmail;
        return (
          <div key={key} className="flex flex-col gap-1.5">
            <Label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
              {t(`fields.${key}.label`)}
            </Label>
            <Input
              id={inputId}
              type={type}
              autoComplete={autoComplete}
              placeholder={t(`fields.${key}.placeholder`)}
              aria-invalid={!!message}
              aria-describedby={
                [locked && `${inputId}-locked`, message && `${inputId}-error`]
                  .filter(Boolean)
                  .join(' ') || undefined
              }
              className="h-11 border-border/80 bg-background read-only:cursor-default read-only:bg-muted"
              readOnly={locked}
              rightIcon={
                locked ? <Lock aria-hidden className="size-4 text-muted-foreground" /> : undefined
              }
              {...register(key)}
            />
            {locked && (
              <InvitedEmailHint id={`${inputId}-locked`} onRelease={releaseInvitedEmail} />
            )}
            {message && (
              <p id={`${inputId}-error`} role="alert" className="text-xs text-destructive">
                {message}
              </p>
            )}
            {key === 'password' && !message && (
              <p className="text-xs text-muted-foreground">{t('password_hint')}</p>
            )}
          </div>
        );
      })}

      <Button type="submit" disabled={submitting} className="h-11 w-full rounded-lg text-[14px]">
        {submitting ? (
          <>
            <Loader2 className="animate-spin" />
            {t('submitting')}
          </>
        ) : (
          t('submit')
        )}
      </Button>

      {emailTaken && <EmailTakenNotice locale={locale} />}

      <div className="text-center text-xs text-muted-foreground">
        <Link
          href={`/${locale}/login`}
          className="hover:text-foreground underline-offset-4 hover:underline"
        >
          {t('links.login')}
        </Link>
      </div>
    </form>
  );
}
